// Core packages
import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { FormGroup } from "@angular/forms";

// Third party packages
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from "@angular/material";
import { FormlyFieldConfig, FormlyFormOptions } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import { MatProgressButtonOptions } from "mat-progress-buttons";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";
import { finalize, take } from "rxjs/operators";

// Custom packages
import {
  DocumentTemplateMoveDialogComponent,
  DocumentTemplateMoveDialogData,
  DocumentTemplateMoveDialogGroup,
} from "src/app/modules/settings/components/document-template-move-dialog/document-template-move-dialog.component";
import {
  DocumentTemplateFieldFactory,
  FieldOption,
  LabelConflictChecker,
} from "src/app/modules/settings/document-template.fields";
import { DocumentTemplateService } from "src/app/modules/settings/document-template.service";
import {
  buildDataTypeLabels,
  descriptionForCreate,
  descriptionForUpdate,
  findLabelConflict,
  readDescription,
} from "src/app/modules/settings/document-template.utils";
import { ConfirmDialogComponent } from "src/app/shared/components/dialogs/confirm-dialog/confirm-dialog.component";
import { DataTypeSummary } from "src/app/shared/interfaces/data-type.interface";
import {
  DocumentTemplateArea,
  DocumentTemplateContent,
  DocumentTemplateContentUpdatePayload,
  DocumentTemplateDoc,
  DocumentTemplateDocCreatePayload,
  DocumentTemplateDocUpdatePayload,
  DocumentTemplateStructureOption,
} from "src/app/shared/interfaces/document-template.interface";
import { ConfigService } from "src/app/shared/services/config.service";
import { DataTypesService } from "src/app/shared/services/data-types.service";

/** Data the third level dialog needs */
export interface DocumentTemplateDocsDialogData {
  content: DocumentTemplateContent;
  /** Area the section belongs to, used as fallback label of the move picker */
  area: DocumentTemplateArea;
  /** Sections of that area, fallback destinations when the tree cannot be fetched */
  contents: DocumentTemplateContent[];
  condoStructures: DocumentTemplateStructureOption[];
  includeDeleted: boolean;
}

/** Which document editor the dialog is showing */
type DocPanel = "none" | "doc" | "newDoc";

/** Quick picks offered next to the free text "expiration period" field */
const EXPIRATION_PERIOD_SUGGESTIONS = ["1a", "2a", "3a", "5a", "10a", "15a", "20a", "30a"];

/**
 * Third level of the document template: the documents of a single section, plus the
 * editor of the section itself.
 *
 * Documents are fetched when the dialog opens (GET /docs?contentId=) and every write
 * is a single call on a single node: nothing here ever reloads the whole template.
 *
 * @since 3.0.0
 */
@Component({
  selector: "app-document-template-docs-dialog",
  templateUrl: "./document-template-docs-dialog.component.html",
  styleUrls: ["./document-template-docs-dialog.component.scss"],
})
export class DocumentTemplateDocsDialogComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  lang = this.translateService.currentLang || this.translateService.defaultLang;

  /** Section being edited. Refreshed in place after every successful save. */
  content: DocumentTemplateContent;

  docs: DocumentTemplateDoc[] = [];
  selectedDoc: DocumentTemplateDoc = null;

  // BEGIN - Data types catalogue
  /** Whole catalogue, DELETED ROWS INCLUDED: they still have to be resolved by id */
  dataTypes: DataTypeSummary[] = [];
  /** The ones that can actually be assigned to a document */
  selectableDataTypes: DataTypeSummary[] = [];
  /** Readable label of every data type of the catalogue, indexed by id */
  private dataTypeLabels: { [dataTypeId: string]: string } = {};
  dataTypesAreLoading = true;
  /** Set when the catalogue could not be loaded: shown inline, never swallowed */
  dataTypesErrorMessage = "";
  // END - Data types catalogue

  docsAreLoading = true;
  reorderIsSaving = false;
  /** Set while the destination list of a move is being fetched */
  moveIsLoading = false;
  includeDeleted = false;

  docPanel: DocPanel = "none";

  /** Identifiers stay read only until the user explicitly unlocks them */
  contentIdentifiersUnlocked = false;
  docIdentifiersUnlocked = false;

  /** Set as soon as the section changes, so the caller knows it has to reload it */
  private contentHasChanged = false;

  /**
   * Sections of the area this one belongs to, HIDDEN ONES INCLUDED: the labels of a
   * section are unique inside its area, and the back-end refuses a label held by a soft
   * deleted section too. The list handed over by the page follows the "show hidden"
   * filter instead, so it cannot be used as is.
   */
  private contentsForValidation: DocumentTemplateContent[] = [];
  /** Set when the snapshot could not be fetched: the check degrades, it never lies */
  contentCheckIsDegraded = false;

  expirationPeriodSuggestions = EXPIRATION_PERIOD_SUGGESTIONS;

  private fieldFactory = new DocumentTemplateFieldFactory(
    this.translateService
  );

  // BEGIN - Section (level 2) form
  contentForm: FormGroup = new FormGroup({});
  contentModel: any = {};
  contentFields: FormlyFieldConfig[] = [];
  contentOptions: FormlyFormOptions = {};
  contentBtnOptions: MatProgressButtonOptions = this.buildSaveButton();
  // END - Section (level 2) form

  // BEGIN - Document (level 3) edit form
  docForm: FormGroup = new FormGroup({});
  docModel: any = {};
  docFields: FormlyFieldConfig[] = [];
  docOptions: FormlyFormOptions = {};
  docBtnOptions: MatProgressButtonOptions = this.buildSaveButton();
  // END - Document (level 3) edit form

  // BEGIN - Document (level 3) creation form
  newDocForm: FormGroup = new FormGroup({});
  newDocModel: any = {};
  newDocFields: FormlyFieldConfig[] = [];
  newDocOptions: FormlyFormOptions = {};
  newDocBtnOptions: MatProgressButtonOptions = this.buildSaveButton();
  // END - Document (level 3) creation form

  /**
   * Class constructor
   */
  constructor(
    public dialogRef: MatDialogRef<DocumentTemplateDocsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DocumentTemplateDocsDialogData,
    private configService: ConfigService,
    private cookieService: CookieService,
    private dataTypesService: DataTypesService,
    private dialog: MatDialog,
    private documentTemplateService: DocumentTemplateService,
    private toastrService: ToastrService,
    private translateService: TranslateService
  ) {}

  /**
   * Build a dedicated submit button state (one per form).
   *
   * @since 3.0.0
   */
  private buildSaveButton(): MatProgressButtonOptions {
    return {
      active: false,
      text: this.translateService.instant("common.cta.save"),
      spinnerSize: 19,
      raised: true,
      stroked: false,
      flat: false,
      fab: false,
      buttonColor: "primary",
      spinnerColor: "primary",
      fullWidth: false,
      disabled: false,
      mode: "indeterminate",
      buttonIcon: {
        fontSet: "fal",
        fontIcon: "fa-save",
        inline: true,
      },
    };
  }

  /**
   * Init component
   *
   * @since 3.0.0
   */
  ngOnInit(): void {
    this.lang =
      this.cookieService.get("lang") || this.configService.defaultLang;
    this.content = this.data.content;
    this.includeDeleted = !!this.data.includeDeleted;
    this.buildContentFields();
    this.loadContentsForValidation();
    this.loadDataTypes();
    this.loadDocs();
  }

  /**
   * Load the sections the label check runs against: the siblings of this one, inside
   * the area it currently belongs to, hidden ones included.
   *
   * On failure it falls back on the list the page handed over, which carries the active
   * siblings at least, and the form says the check is only partial.
   *
   * @since 3.0.0
   */
  private loadContentsForValidation(): void {
    this.subscriptions.push(
      this.documentTemplateService
        .listContents(this.content.areaId, true)
        .subscribe(
          (res) => {
            this.contentsForValidation =
              res && Array.isArray(res.data) ? res.data : [];
            this.contentCheckIsDegraded = false;
          },
          () => {
            this.contentsForValidation = Array.isArray(this.data.contents)
              ? this.data.contents
              : [];
            this.contentCheckIsDegraded = true;
          }
        )
    );
  }

  /**
   * Handle component destroy
   *
   * @since 3.0.0
   */
  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Close the dialog, telling the caller whether the section list has to be reloaded.
   *
   * @since 3.0.0
   */
  onClose(): void {
    this.dialogRef.close({ changed: this.contentHasChanged });
  }

  // BEGIN - Data types catalogue

  /**
   * Load the data types catalogue feeding the "data type" select.
   *
   * Deleted rows are requested too: a document type keeps referencing the data type it
   * was pointing at even after that one is deleted, and the editor has to be able to
   * name it instead of showing an empty select.
   *
   * The catalogue is legitimately empty until the back-end import has been run in the
   * environment: that is not an error, it only means no document can be created yet.
   *
   * @since 3.0.0
   */
  loadDataTypes(): void {
    this.dataTypesAreLoading = true;
    this.dataTypesErrorMessage = "";
    this.subscriptions.push(
      this.dataTypesService
        .listSummaries(true)
        .pipe(finalize(() => (this.dataTypesAreLoading = false)))
        .subscribe(
          (res) => {
            this.dataTypes = res && Array.isArray(res.data) ? res.data : [];
            this.selectableDataTypes = this.dataTypes.filter(
              (dataType) => dataType && dataType._id && !dataType.deleted
            );
            this.dataTypeLabels = buildDataTypeLabels(this.dataTypes);
          },
          (err) => {
            this.dataTypes = [];
            this.selectableDataTypes = [];
            this.dataTypeLabels = {};
            this.dataTypesErrorMessage = this.buildDataTypesErrorMessage(err);
          }
        )
    );
  }

  /**
   * Message shown when the catalogue cannot be loaded. The two permissions it needs
   * (canReadDocumentsList AND canCreateSettings) are not the ones guarding this page,
   * so a 403 here is a plausible configuration issue and deserves its own wording.
   *
   * @since 3.0.0
   */
  private buildDataTypesErrorMessage(err: any): string {
    const status = err && typeof err.status !== "undefined" ? err.status : 0;
    if (status === 401 || status === 403) {
      return this.translateService.instant(
        "settings.documentTemplate.errors.dataTypesForbidden"
      );
    }
    const message =
      err && err.error && typeof err.error.message === "string"
        ? err.error.message
        : "";
    return message
      ? message
      : this.translateService.instant(
          "settings.documentTemplate.errors.dataTypesUnavailable"
        );
  }

  /**
   * True while either the documents or the catalogue are still being fetched: the
   * editors cannot be opened before both are in, or the select would be built on an
   * empty catalogue.
   *
   * @since 3.0.0
   */
  get docsPanelIsLoading(): boolean {
    return this.docsAreLoading || this.dataTypesAreLoading;
  }

  /**
   * True when the catalogue loaded fine but holds nothing assignable: no document can
   * be created until the back-end import has been run.
   *
   * @since 3.0.0
   */
  get dataTypeCatalogIsEmpty(): boolean {
    return (
      !this.dataTypesAreLoading &&
      !this.dataTypesErrorMessage &&
      !this.selectableDataTypes.length
    );
  }

  /**
   * Readable label of a data type id, '' when the id is unknown to the catalogue or
   * simply absent. Never invents a placeholder: a document type without a data type is
   * a legitimate case, not a missing value to fill in.
   *
   * @since 3.0.0
   */
  dataTypeLabelFor(dataTypeId: string): string {
    if (!dataTypeId) {
      return "";
    }
    const label = this.dataTypeLabels[dataTypeId.toString()];
    return label ? label : "";
  }

  /**
   * True when the document references a data type that cannot be assigned anymore
   * (deleted, or gone from the catalogue altogether). Stays false while the catalogue
   * is unusable, otherwise every document would look broken.
   *
   * @since 3.0.0
   */
  dataTypeIsUnavailable(dataTypeId: string): boolean {
    if (
      !dataTypeId ||
      this.dataTypesAreLoading ||
      this.dataTypesErrorMessage
    ) {
      return false;
    }
    const id = dataTypeId.toString();
    return !this.selectableDataTypes.some(
      (dataType) => dataType._id.toString() === id
    );
  }

  // END - Data types catalogue

  // BEGIN - Level 3 loading

  /**
   * Load the documents of the section.
   *
   * @since 3.0.0
   */
  loadDocs(): void {
    this.docsAreLoading = true;
    this.subscriptions.push(
      this.documentTemplateService
        .listDocs(this.content._id, this.includeDeleted)
        .pipe(finalize(() => (this.docsAreLoading = false)))
        .subscribe(
          (res) => {
            // Already ordered by the back-end: the front-end never sorts the list
            this.docs = res && Array.isArray(res.data) ? res.data : [];
            this.refreshSelectedDoc();
          },
          (err) => {
            this.docs = [];
            this.documentTemplateService.handleApiError(err);
          }
        )
    );
  }

  /**
   * Re-point the selected document at its instance inside a freshly returned list,
   * WITHOUT rebuilding the editor: a reorder must not throw away what the user is
   * typing.
   *
   * @since 3.0.0
   */
  private syncSelectedDoc(): void {
    if (!this.selectedDoc) {
      return;
    }
    const found = this.docs.find(
      (doc) => doc._id.toString() === this.selectedDoc._id.toString()
    );
    if (found) {
      this.selectedDoc = found;
    }
  }

  /**
   * Re-sync the open document editor with the freshly loaded list, or close it when
   * the document is gone.
   *
   * @since 3.0.0
   */
  private refreshSelectedDoc(): void {
    if (!this.selectedDoc || this.docPanel !== "doc") {
      return;
    }
    const found = this.docs.find(
      (doc) => doc._id.toString() === this.selectedDoc._id.toString()
    );
    if (!found) {
      this.selectedDoc = null;
      this.docPanel = "none";
      return;
    }
    this.selectedDoc = found;
    this.buildDocFields();
  }

  /**
   * Toggle the "show hidden" filter on the documents list.
   *
   * @since 3.0.0
   */
  onIncludeDeletedChange(): void {
    this.loadDocs();
  }

  // END - Level 3 loading

  // BEGIN - Level 2 editing

  /**
   * Build the section editor. Identifiers join the fields only once unlocked, so a
   * locked form can never send them.
   *
   * @since 3.0.0
   */
  private buildContentFields(): void {
    this.contentForm = new FormGroup({});
    this.contentModel = {
      code: this.content.code,
      elementType: this.content.elementType,
      structure: this.content.structure || "",
      visible: !!this.content.visible,
      expanded: !!this.content.expanded,
      translation: {
        it: this.content.translation ? this.content.translation.it : "",
        en: this.content.translation ? this.content.translation.en : "",
      },
    };

    const fields: FormlyFieldConfig[] = [];
    if (this.contentIdentifiersUnlocked) {
      fields.push({
        fieldGroupClassName: "row",
        fieldGroup: [this.fieldFactory.contentCode("col-12 col-md-4")],
      });
    }
    fields.push({
      fieldGroupClassName: "row",
      fieldGroup: [
        this.fieldFactory.translation(
          "it",
          "col-12 col-md-6",
          this.buildLabelChecker("it")
        ),
        this.fieldFactory.translation(
          "en",
          "col-12 col-md-6",
          this.buildLabelChecker("en")
        ),
        this.fieldFactory.elementType("col-12 col-md-6"),
        this.fieldFactory.structure(
          "col-12 col-md-6",
          this.buildStructureOptions(),
          () => this.contentModel
        ),
        this.fieldFactory.toggle(
          "visible",
          "settings.documentTemplate.fields.visible",
          "col-12 col-md-6"
        ),
        this.fieldFactory.toggle(
          "expanded",
          "settings.documentTemplate.fields.expanded",
          "col-12 col-md-6"
        ),
      ],
    });

    this.contentFields = fields;
  }

  /**
   * Structure options rebuilt from scratch, never appended to.
   *
   * @since 3.0.0
   */
  private buildStructureOptions(): FieldOption[] {
    const structures = Array.isArray(this.data.condoStructures)
      ? this.data.condoStructures
      : [];
    return structures.map((structure) => ({
      value: structure.key,
      label: structure.translation
        ? structure.translation.it || structure.key
        : structure.key,
    }));
  }

  /**
   * Label of the condo structure the section is bound to, shown next to its title.
   *
   * @since 3.0.0
   */
  get linkedStructureLabel(): string {
    if (
      !this.content ||
      this.content.elementType !== "array" ||
      !this.content.structure
    ) {
      return "";
    }
    const structures = Array.isArray(this.data.condoStructures)
      ? this.data.condoStructures
      : [];
    const found = structures.find(
      (structure) => structure.key === this.content.structure
    );
    if (found && found.translation && found.translation.it) {
      return found.translation.it;
    }
    return this.content.structure;
  }

  /**
   * Ask for an explicit confirmation before letting the user touch the section
   * identifiers.
   *
   * @since 3.0.0
   */
  onUnlockContentIdentifiers(): void {
    this.confirmUnlock(() => {
      this.contentIdentifiersUnlocked = true;
      this.buildContentFields();
    });
  }

  /**
   * Save the section, sending only what changed.
   *
   * @since 3.0.0
   */
  onContentSubmit(): void {
    if (!this.markFormAsTouched(this.contentForm)) {
      return;
    }
    // Checked on the model, not on the payload: an "array" section whose elementType
    // did not change carries no `elementType` in the (partial) payload, and it would
    // slip through a check made on the payload alone.
    if (
      this.contentModel.elementType === "array" &&
      !this.contentModel.structure
    ) {
      const control = this.contentForm.get("structure");
      if (control) {
        control.markAsTouched();
        control.setErrors({
          "server-error": this.translateService.instant(
            "common.errors.field_is_required"
          ),
        });
      }
      this.toastrService.error(
        this.translateService.instant("common.errors.form_is_invalid")
      );
      return;
    }

    const payload = this.buildContentUpdatePayload();
    if (!payload) {
      this.toastrService.info(
        this.translateService.instant("settings.documentTemplate.noChanges")
      );
      return;
    }

    const conflictFields = this.collectConflictFields(payload);

    // Only the identifier is destructive outside this application: the labels, which
    // can collide too, go straight through.
    if (typeof payload.code !== "undefined") {
      this.confirmThen(
        this.translateService.instant(
          "settings.documentTemplate.dialogs.saveIdentifier.title"
        ),
        this.translateService.instant(
          "settings.documentTemplate.dialogs.saveIdentifier.message"
        ),
        () => this.persistContent(payload, conflictFields)
      );
      return;
    }
    this.persistContent(payload, conflictFields);
  }

  /**
   * Controls of a payload that could be the duplicated one, so that a 409 highlights
   * where to act: the identifier and the two labels, whichever is being sent.
   *
   * @since 3.0.0
   */
  private collectConflictFields(
    payload: DocumentTemplateContentUpdatePayload
  ): string[] {
    const fields: string[] = [];
    if (typeof payload.code !== "undefined") {
      fields.push("code");
    }
    if (payload.translation) {
      if (typeof payload.translation.it !== "undefined") {
        fields.push("translation.it");
      }
      if (typeof payload.translation.en !== "undefined") {
        fields.push("translation.en");
      }
    }
    return fields;
  }

  /**
   * Label check of this section: the italian and the english labels are unique INSIDE
   * ITS AREA. Two areas legitimately hold a section with the same name.
   *
   * The snapshot is read when the validator runs, so it always reflects the last load.
   *
   * @since 3.0.0
   */
  private buildLabelChecker(language: "it" | "en"): LabelConflictChecker {
    return (value: string): string => {
      const conflict = findLabelConflict(
        this.contentsForValidation,
        language,
        value,
        this.content._id
      );
      if (!conflict) {
        return "";
      }
      // A hidden owner deserves its own wording: the user does not see that section in
      // the list, so its name looks free
      return this.translateService.instant(
        conflict.deleted
          ? "settings.documentTemplate.errors.duplicateContentHidden"
          : "settings.documentTemplate.errors.duplicateContent",
        { name: conflict.label }
      );
    };
  }

  /**
   * Perform the section PUT.
   *
   * @since 3.0.0
   */
  private persistContent(
    payload: DocumentTemplateContentUpdatePayload,
    conflictFields: string[]
  ): void {
    this.contentBtnOptions.active = true;
    this.subscriptions.push(
      this.documentTemplateService
        .updateContent(this.content._id, payload)
        .pipe(finalize(() => (this.contentBtnOptions.active = false)))
        .subscribe(
          (res) => {
            this.toastrService.success(
              res && res.message
                ? res.message
                : this.translateService.instant(
                    "settings.documentTemplate.saved"
                  )
            );
            this.contentHasChanged = true;
            this.contentIdentifiersUnlocked = false;
            if (res && res.data && res.data._id) {
              this.content = res.data;
            }
            this.buildContentFields();
            // The label just saved is now taken: refresh what the check runs against
            this.loadContentsForValidation();
          },
          (err) => {
            this.documentTemplateService.handleApiError(err, {
              form: this.contentForm,
              conflictFields,
            });
          }
        )
    );
  }

  /**
   * Diff the section editor against the loaded section. Returns null when nothing
   * changed.
   *
   * @since 3.0.0
   */
  private buildContentUpdatePayload(): DocumentTemplateContentUpdatePayload {
    const model = this.contentModel || {};
    const payload: DocumentTemplateContentUpdatePayload = {};
    let changed = false;

    if (this.contentIdentifiersUnlocked && model.code !== this.content.code) {
      payload.code = model.code;
      changed = true;
    }
    if (model.elementType !== this.content.elementType) {
      payload.elementType = model.elementType;
      changed = true;
    }
    const structure = model.elementType === "array" ? model.structure : "";
    if (structure !== (this.content.structure || "")) {
      payload.structure = structure;
      changed = true;
    }
    if (!!model.visible !== !!this.content.visible) {
      payload.visible = !!model.visible;
      changed = true;
    }
    if (!!model.expanded !== !!this.content.expanded) {
      payload.expanded = !!model.expanded;
      changed = true;
    }
    const translation = this.buildTranslationPayload(
      model.translation,
      this.content.translation
    );
    if (translation) {
      payload.translation = translation;
      changed = true;
    }

    return changed ? payload : null;
  }

  // END - Level 2 editing

  // BEGIN - Level 3 editing

  /**
   * Open a document in the editor.
   *
   * @since 3.0.0
   */
  onDocClicked(doc: DocumentTemplateDoc): void {
    this.selectedDoc = doc;
    this.docPanel = "doc";
    this.docIdentifiersUnlocked = false;
    this.buildDocFields();
  }

  /**
   * Build the document editor.
   *
   * @since 3.0.0
   */
  private buildDocFields(): void {
    const doc = this.selectedDoc;
    if (!doc) {
      return;
    }
    this.docForm = new FormGroup({});
    this.docModel = {
      key: doc.key,
      dataTypeId: doc.dataTypeId ? doc.dataTypeId : "",
      mandatory: !!doc.mandatory,
      contract: !!doc.contract,
      privacy: !!doc.privacy,
      expirationNotice: doc.expirationNotice ? doc.expirationNotice : "",
      expirationPeriod: doc.expirationPeriod ? doc.expirationPeriod : "",
      translation: {
        it: doc.translation ? doc.translation.it : "",
        en: doc.translation ? doc.translation.en : "",
      },
      // Absent on every document type nobody described yet: the field starts empty
      description: readDescription(doc.description),
    };

    const fields: FormlyFieldConfig[] = [];
    if (this.docIdentifiersUnlocked) {
      fields.push({
        fieldGroupClassName: "row",
        fieldGroup: [this.fieldFactory.docKey("col-12 col-md-4")],
      });
    }
    fields.push({
      fieldGroupClassName: "row",
      fieldGroup: this.buildDocCommonFields(
        doc.dataTypeId ? doc.dataTypeId : "",
        false
      ),
    });
    this.docFields = fields;
  }

  /**
   * Fields shared by the document editor and the document creation form.
   *
   * @since 3.0.0
   *
   * @param currentDataTypeId  dataTypeId stored on the document ('' when creating)
   * @param dataTypeIsRequired True on creation only: the back-end demands the reference
   *                           on POST, while on PUT it accepts a document that has none
   *                           (and refuses to empty one that has it). Requiring it while
   *                           editing would block every other change on the documents
   *                           that legitimately carry no data entry form.
   */
  private buildDocCommonFields(
    currentDataTypeId: string,
    dataTypeIsRequired: boolean
  ): FormlyFieldConfig[] {
    return [
      this.fieldFactory.translation("it", "col-12 col-md-6"),
      this.fieldFactory.translation("en", "col-12 col-md-6"),
      this.fieldFactory.description("col-12"),
      this.fieldFactory.dataTypeId(
        "col-12 col-md-4",
        this.dataTypes,
        currentDataTypeId,
        dataTypeIsRequired
      ),
      this.fieldFactory.expirationNotice("col-12 col-md-4"),
      this.fieldFactory.expirationPeriod("col-12 col-md-4"),
      this.fieldFactory.toggle(
        "mandatory",
        "settings.documentTemplate.fields.mandatory",
        "col-12 col-md-4"
      ),
      this.fieldFactory.toggle(
        "contract",
        "settings.documentTemplate.fields.contract",
        "col-12 col-md-4"
      ),
      this.fieldFactory.toggle(
        "privacy",
        "settings.documentTemplate.fields.privacy",
        "col-12 col-md-4"
      ),
    ];
  }

  /**
   * Ask for an explicit confirmation before letting the user touch the document key.
   *
   * @since 3.0.0
   */
  onUnlockDocIdentifiers(): void {
    this.confirmUnlock(() => {
      this.docIdentifiersUnlocked = true;
      this.buildDocFields();
    });
  }

  /**
   * Write the given expiration period into the form currently open.
   *
   * @since 3.0.0
   */
  onPickExpirationPeriod(value: string, isNewDoc: boolean): void {
    const form = isNewDoc ? this.newDocForm : this.docForm;
    const control = form.get("expirationPeriod");
    if (control) {
      control.setValue(value);
      control.markAsDirty();
      return;
    }
    if (isNewDoc) {
      this.newDocModel.expirationPeriod = value;
    } else {
      this.docModel.expirationPeriod = value;
    }
  }

  /**
   * Save the document, sending only what changed.
   *
   * @since 3.0.0
   */
  onDocSubmit(): void {
    if (!this.selectedDoc) {
      return;
    }
    if (!this.markFormAsTouched(this.docForm)) {
      return;
    }
    const payload = this.buildDocUpdatePayload();
    if (!payload) {
      this.toastrService.info(
        this.translateService.instant("settings.documentTemplate.noChanges")
      );
      return;
    }

    const conflictFields: string[] = [];
    if (typeof payload.key !== "undefined") {
      conflictFields.push("key");
    }

    if (conflictFields.length) {
      this.confirmThen(
        this.translateService.instant(
          "settings.documentTemplate.dialogs.saveIdentifier.title"
        ),
        this.translateService.instant(
          "settings.documentTemplate.dialogs.saveIdentifier.message"
        ),
        () => this.persistDoc(payload, conflictFields)
      );
      return;
    }
    this.persistDoc(payload, conflictFields);
  }

  /**
   * Perform the document PUT.
   *
   * @since 3.0.0
   */
  private persistDoc(
    payload: DocumentTemplateDocUpdatePayload,
    conflictFields: string[]
  ): void {
    this.docBtnOptions.active = true;
    this.subscriptions.push(
      this.documentTemplateService
        .updateDoc(this.selectedDoc._id, payload)
        .pipe(finalize(() => (this.docBtnOptions.active = false)))
        .subscribe(
          (res) => {
            this.toastrService.success(
              res && res.message
                ? res.message
                : this.translateService.instant(
                    "settings.documentTemplate.saved"
                  )
            );
            this.docIdentifiersUnlocked = false;
            this.loadDocs();
          },
          (err) => {
            this.documentTemplateService.handleApiError(err, {
              form: this.docForm,
              conflictFields,
              onNotFound: () => this.loadDocs(),
            });
          }
        )
    );
  }

  /**
   * Diff the document editor against the loaded document. Returns null when nothing
   * changed.
   *
   * @since 3.0.0
   */
  private buildDocUpdatePayload(): DocumentTemplateDocUpdatePayload {
    const doc = this.selectedDoc;
    const model = this.docModel || {};
    const payload: DocumentTemplateDocUpdatePayload = {};
    let changed = false;

    if (this.docIdentifiersUnlocked && model.key !== doc.key) {
      payload.key = model.key;
      changed = true;
    }
    // Only ever REPLACED, never emptied: the back-end drops a null and rejects a ''.
    // A document that carries no data type keeps carrying none unless one is picked.
    const dataTypeId = model.dataTypeId ? model.dataTypeId.toString() : "";
    const currentDataTypeId = doc.dataTypeId ? doc.dataTypeId.toString() : "";
    if (dataTypeId && dataTypeId !== currentDataTypeId) {
      payload.dataTypeId = dataTypeId;
      changed = true;
    }
    if (!!model.mandatory !== !!doc.mandatory) {
      payload.mandatory = !!model.mandatory;
      changed = true;
    }
    if (!!model.contract !== !!doc.contract) {
      payload.contract = !!model.contract;
      changed = true;
    }
    if (!!model.privacy !== !!doc.privacy) {
      payload.privacy = !!model.privacy;
      changed = true;
    }
    const notice = model.expirationNotice ? model.expirationNotice : "";
    if (notice !== (doc.expirationNotice ? doc.expirationNotice : "")) {
      payload.expirationNotice = notice;
      changed = true;
    }
    const period = model.expirationPeriod ? model.expirationPeriod : "";
    if (period !== (doc.expirationPeriod ? doc.expirationPeriod : "")) {
      payload.expirationPeriod = period;
      changed = true;
    }
    const translation = this.buildTranslationPayload(
      model.translation,
      doc.translation
    );
    if (translation) {
      payload.translation = translation;
      changed = true;
    }
    const description = descriptionForUpdate(
      model.description,
      doc.description
    );
    if (typeof description !== "undefined") {
      // '' included: it is the only value that clears a stored description
      payload.description = description;
      changed = true;
    }

    return changed ? payload : null;
  }

  /**
   * Open the "new document" editor, with its own form instance.
   *
   * @since 3.0.0
   */
  onAddDocClicked(): void {
    // The data type is mandatory on POST: with an empty catalogue the form could only
    // ever produce a 400, so the panel does not even open.
    if (!this.selectableDataTypes.length) {
      this.toastrService.error(
        this.dataTypesErrorMessage
          ? this.dataTypesErrorMessage
          : this.translateService.instant(
              "settings.documentTemplate.hints.dataTypeCatalogEmpty"
            )
      );
      return;
    }
    this.docPanel = "newDoc";
    this.selectedDoc = null;
    this.newDocForm = new FormGroup({});
    this.newDocModel = {
      key: "",
      dataTypeId: "",
      mandatory: false,
      contract: false,
      privacy: false,
      expirationNotice: "",
      expirationPeriod: "",
      translation: { it: "", en: "" },
      description: "",
    };
    this.newDocFields = [
      {
        fieldGroupClassName: "row",
        fieldGroup: [this.fieldFactory.docKey("col-12 col-md-4")],
      },
      {
        fieldGroupClassName: "row",
        fieldGroup: this.buildDocCommonFields("", true),
      },
    ];
  }

  /**
   * Create the document under the current section. `position` is omitted: the
   * back-end appends it at the end.
   *
   * @since 3.0.0
   */
  onNewDocSubmit(): void {
    if (!this.markFormAsTouched(this.newDocForm)) {
      return;
    }
    const model = this.newDocModel || {};
    const payload: DocumentTemplateDocCreatePayload = {
      contentId: this.content._id,
      key: model.key,
      dataTypeId: model.dataTypeId,
      mandatory: !!model.mandatory,
      contract: !!model.contract,
      privacy: !!model.privacy,
      expirationNotice: model.expirationNotice ? model.expirationNotice : "",
      expirationPeriod: model.expirationPeriod ? model.expirationPeriod : "",
      translation: {
        it: model.translation ? model.translation.it : "",
        en: model.translation ? model.translation.en : "",
      },
    };
    const description = descriptionForCreate(model.description);
    if (typeof description !== "undefined") {
      payload.description = description;
    }
    this.newDocBtnOptions.active = true;
    this.subscriptions.push(
      this.documentTemplateService
        .createDoc(payload)
        .pipe(finalize(() => (this.newDocBtnOptions.active = false)))
        .subscribe(
          (res) => {
            this.toastrService.success(
              res && res.message
                ? res.message
                : this.translateService.instant(
                    "settings.documentTemplate.created"
                  )
            );
            this.docPanel = "none";
            this.loadDocs();
          },
          (err) => {
            this.documentTemplateService.handleApiError(err, {
              form: this.newDocForm,
              conflictFields: ["key"],
            });
          }
        )
    );
  }

  /**
   * Hide a document. No cascade at this level.
   *
   * @since 3.0.0
   */
  onDeleteDoc(doc: DocumentTemplateDoc): void {
    this.confirmThen(
      this.translateService.instant(
        "settings.documentTemplate.dialogs.deleteDoc.title"
      ),
      this.translateService.instant(
        "settings.documentTemplate.dialogs.deleteDoc.message",
        { name: this.nodeLabel(doc) }
      ),
      () => {
        this.subscriptions.push(
          this.documentTemplateService.deleteDoc(doc._id).subscribe(
            (res) => {
              this.toastrService.success(
                res && res.message
                  ? res.message
                  : this.translateService.instant(
                      "settings.documentTemplate.deleted"
                    )
              );
              if (
                this.selectedDoc &&
                this.selectedDoc._id.toString() === doc._id.toString()
              ) {
                this.docPanel = "none";
                this.selectedDoc = null;
              }
              this.loadDocs();
            },
            (err) => {
              this.documentTemplateService.handleApiError(err, {
                onNotFound: () => this.loadDocs(),
              });
            }
          )
        );
      },
      "warn"
    );
  }

  /**
   * Restore a hidden document. Blocked (and the button disabled) while its section is
   * hidden: the back-end answers 400 in that case.
   *
   * @since 3.0.0
   */
  onRestoreDoc(doc: DocumentTemplateDoc): void {
    this.subscriptions.push(
      this.documentTemplateService.restoreDoc(doc._id).subscribe(
        (res) => {
          this.toastrService.success(
            res && res.message
              ? res.message
              : this.translateService.instant(
                  "settings.documentTemplate.restored"
                )
          );
          this.loadDocs();
        },
        (err) => {
          this.documentTemplateService.handleApiError(err, {
            onNotFound: () => this.loadDocs(),
          });
        }
      )
    );
  }

  /**
   * True when a document cannot be restored because its section is still hidden.
   *
   * @since 3.0.0
   */
  get restoreDocIsBlocked(): boolean {
    return !!(this.content && this.content.deleted);
  }

  /**
   * Move a document into another section: a single PUT with `{ contentId }`.
   *
   * The destination list spans every area, so the whole tree is fetched once, on
   * demand, only for this deliberate action. If that call fails the picker degrades
   * to the sections of the current area, which are already in memory.
   *
   * @since 3.0.0
   */
  onMoveDoc(doc: DocumentTemplateDoc): void {
    this.moveIsLoading = true;
    this.subscriptions.push(
      this.documentTemplateService
        .getTree(false)
        .pipe(finalize(() => (this.moveIsLoading = false)))
        .subscribe(
          (res) => {
            this.openMoveDocDialog(doc, this.buildDestinationsFromTree(res));
          },
          () => {
            this.openMoveDocDialog(doc, this.buildDestinationsFromCurrentArea());
          }
        )
    );
  }

  /**
   * Sections of every area, grouped by area, minus the current one.
   *
   * @since 3.0.0
   */
  private buildDestinationsFromTree(
    res: any
  ): DocumentTemplateMoveDialogGroup[] {
    const areas =
      res && res.data && Array.isArray(res.data.area) ? res.data.area : [];
    const groups: DocumentTemplateMoveDialogGroup[] = [];
    areas.forEach((area: any) => {
      if (!area || !Array.isArray(area.content)) {
        return;
      }
      const options = area.content
        .filter(
          (content: any) =>
            content &&
            content._id &&
            content._id.toString() !== this.content._id.toString()
        )
        .map((content: any) => ({
          value: content._id,
          label: this.nodeLabel(content),
          hint: content.code,
          disabled: false,
        }));
      if (options.length) {
        groups.push({ label: this.nodeLabel(area), options });
      }
    });
    return groups;
  }

  /**
   * Fallback destinations: the sections of the area the dialog was opened from.
   *
   * @since 3.0.0
   */
  private buildDestinationsFromCurrentArea(): DocumentTemplateMoveDialogGroup[] {
    const contents = Array.isArray(this.data.contents)
      ? this.data.contents
      : [];
    const options = contents
      .filter(
        (content) => content._id.toString() !== this.content._id.toString()
      )
      .map((content) => ({
        value: content._id,
        label: this.nodeLabel(content),
        hint: content.code,
        disabled: false,
      }));
    if (!options.length) {
      return [];
    }
    return [{ label: this.nodeLabel(this.data.area), options }];
  }

  /**
   * Open the destination picker and perform the move.
   *
   * @since 3.0.0
   */
  private openMoveDocDialog(
    doc: DocumentTemplateDoc,
    groups: DocumentTemplateMoveDialogGroup[]
  ): void {
    const data: DocumentTemplateMoveDialogData = {
      title: this.translateService.instant(
        "settings.documentTemplate.move.docTitle"
      ),
      subject: this.nodeLabel(doc),
      currentLabel: this.nodeLabel(this.content),
      selectLabel: this.translateService.instant(
        "settings.documentTemplate.move.destContent"
      ),
      warning: this.translateService.instant(
        "settings.documentTemplate.move.warning"
      ),
      groups,
    };

    const dialogRef = this.dialog.open(DocumentTemplateMoveDialogComponent, {
      data,
      width: "560px",
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((destContentId) => {
        if (!destContentId) {
          return;
        }
        this.subscriptions.push(
          this.documentTemplateService
            .updateDoc(doc._id, { contentId: destContentId })
            .subscribe(
              (res) => {
                this.toastrService.success(
                  res && res.message
                    ? res.message
                    : this.translateService.instant(
                        "settings.documentTemplate.moved"
                      )
                );
                if (
                  this.selectedDoc &&
                  this.selectedDoc._id.toString() === doc._id.toString()
                ) {
                  this.docPanel = "none";
                  this.selectedDoc = null;
                }
                this.loadDocs();
              },
              (err) => {
                this.documentTemplateService.handleApiError(err, {
                  onNotFound: () => this.loadDocs(),
                });
              }
            )
        );
      });
  }

  /**
   * Persist the new order of level 3.
   *
   * @since 3.0.0
   */
  onDocDropped(event: CdkDragDrop<DocumentTemplateDoc[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }
    // Optimistic reorder, then let the back-end persist the whole sequence at once.
    moveItemInArray(this.docs, event.previousIndex, event.currentIndex);

    this.reorderIsSaving = true;
    this.subscriptions.push(
      this.documentTemplateService
        .reorderDocs(
          this.content._id,
          this.docs.map((doc) => doc._id),
          this.includeDeleted
        )
        .pipe(finalize(() => (this.reorderIsSaving = false)))
        .subscribe(
          (res) => {
            // The response IS the list, already sorted: take it as the truth
            if (res && Array.isArray(res.data)) {
              this.docs = res.data;
              this.syncSelectedDoc();
            }
            this.toastrService.success(
              this.translateService.instant(
                "settings.documentTemplate.reordered"
              )
            );
          },
          (err) => {
            this.documentTemplateService.handleApiError(err);
            this.loadDocs();
          }
        )
    );
  }

  // END - Level 3 editing

  // BEGIN - Shared helpers

  /**
   * Label of a node in the current interface language.
   *
   * @since 3.0.0
   */
  nodeLabel(node: any): string {
    if (!node) {
      return "";
    }
    const translation = node.translation || {};
    if (this.lang === "en" && translation.en) {
      return translation.en;
    }
    if (translation.it) {
      return translation.it;
    }
    if (translation.en) {
      return translation.en;
    }
    // `title` is deliberately not a fallback: it is an internal identifier
    return node.code || node.key || "";
  }

  /**
   * Mark every control as touched and refuse an invalid submit, always telling the
   * user why.
   *
   * @since 3.0.0
   */
  private markFormAsTouched(form: FormGroup): boolean {
    if (!form) {
      return false;
    }
    Object.keys(form.controls).forEach((key) => {
      form.controls[key].markAsTouched();
      form.controls[key].markAsDirty();
    });
    if (form.invalid) {
      this.toastrService.error(
        this.translateService.instant("common.errors.form_is_invalid")
      );
      return false;
    }
    return true;
  }

  /**
   * Keep only the languages that really changed (the back-end merges).
   *
   * @since 3.0.0
   */
  private buildTranslationPayload(model: any, current: any): any {
    const source = model || {};
    const original = current || {};
    const payload: any = {};
    let changed = false;
    if (source.it !== original.it) {
      payload.it = source.it;
      changed = true;
    }
    if (source.en !== original.en) {
      payload.en = source.en;
      changed = true;
    }
    return changed ? payload : null;
  }

  /**
   * Confirmation shown before unlocking an identifier.
   *
   * @since 3.0.0
   */
  private confirmUnlock(action: () => void): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant(
          "settings.documentTemplate.dialogs.unlockIdentifier.title"
        ),
        message: this.translateService.instant(
          "settings.documentTemplate.dialogs.unlockIdentifier.message"
        ),
        btnOkText: this.translateService.instant(
          "settings.documentTemplate.actions.unlockIdentifier"
        ),
        btnCancelText: this.translateService.instant("common.cta.undo"),
        btnOkColor: "warn",
        btnOkIcon: "fa-unlock",
      },
      width: "560px",
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((confirmed) => {
        if (confirmed) {
          action();
        }
      });
  }

  /**
   * Open a confirmation dialog and run `action` when the user confirms.
   *
   * @since 3.0.0
   */
  private confirmThen(
    title: string,
    message: string,
    action: () => void,
    okColor?: string
  ): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title,
        message,
        btnOkText: this.translateService.instant("common.cta.confirm"),
        btnCancelText: this.translateService.instant("common.cta.undo"),
        btnOkColor: okColor || "primary",
      },
      width: "560px",
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((confirmed) => {
        if (confirmed) {
          action();
        }
      });
  }

  // END - Shared helpers
}
